import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Crown,
  Star,
  Gift,
  Truck,
  Shield,
  Tag,
  Calendar,
  TrendingUp,
} from 'lucide-react';
import { useTiers } from '../../../api/queries/loyalty.queries';
import {
  useUpsertTier,
  type TierConfigInput,
} from '../../../api/mutations/loyalty.mutations';
import { useAuthStore } from '../../../stores/auth.store';

interface TierConfigModalProps {
  tier: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function TierConfigModal({ tier, onClose, onSuccess }: TierConfigModalProps) {
  const { t } = useTranslation();
  const { user } = useAuthStore();

  const { data: tiers } = useTiers();
  const upsertMutation = useUpsertTier();

  const existingTier = tiers?.find((t) => t.tier === tier);

  const [form, setForm] = useState<TierConfigInput>({
    tier: (tier as any) || 'bronze',
    name: { en: '', th: '' },
    minLifetimePoints: 0,
    earnRate: 1,
    earnMultiplier: 1,
    redeemRate: 100,
    birthdayBonus: 0,
    birthdayMultiplier: 1,
    welcomeBonus: 0,
    monthlyBonus: 0,
    discountPercentage: 0,
    freeUpgradeCount: 0,
    prioritySupport: false,
    exclusivePromotions: false,
    earlyAccess: false,
    freeDelivery: false,
    birthdayFreeItem: false,
    pointsExpirationMonths: 12,
    gracePeriodonths: 3,
    sortOrder: 0,
  });

  useEffect(() => {
    if (existingTier) {
      setForm({
        tier: existingTier.tier,
        name: existingTier.name,
        minLifetimePoints: existingTier.minLifetimePoints,
        minLifetimeSpend: existingTier.minLifetimeSpend,
        minOrdersCount: existingTier.minOrdersCount,
        earnRate: existingTier.earnRate,
        earnMultiplier: existingTier.earnMultiplier,
        redeemRate: existingTier.redeemRate,
        birthdayBonus: existingTier.birthdayBonus,
        birthdayMultiplier: existingTier.birthdayMultiplier,
        welcomeBonus: existingTier.welcomeBonus,
        monthlyBonus: existingTier.monthlyBonus,
        discountPercentage: existingTier.discountPercentage,
        freeUpgradeCount: existingTier.freeUpgradeCount,
        prioritySupport: existingTier.prioritySupport,
        exclusivePromotions: existingTier.exclusivePromotions,
        earlyAccess: existingTier.earlyAccess,
        freeDelivery: existingTier.freeDelivery,
        birthdayFreeItem: existingTier.birthdayFreeItem,
        pointsExpirationMonths: existingTier.pointsExpirationMonths,
        gracePeriodonths: existingTier.gracePeriodonths,
        color: existingTier.color,
        sortOrder: existingTier.sortOrder,
      });
    }
  }, [existingTier]);

  const getTierColor = (tierName: string) => {
    switch (tierName) {
      case 'diamond':
        return 'text-sky-600 bg-sky-50 border-sky-200';
      case 'platinum':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'gold':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'silver':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-orange-600 bg-orange-50 border-orange-200';
    }
  };

  const handleSubmit = async () => {
    try {
      await upsertMutation.mutateAsync(form);
      onSuccess();
    } catch (error) {
      console.error('Failed to save tier config:', error);
    }
  };

  const canManage = user?.role === 'HQ_ADMIN';

  if (!canManage) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className={`border-b p-4 flex items-center justify-between ${getTierColor(form.tier)}`}>
          <div className="flex items-center gap-3">
            <Crown className="h-6 w-6" />
            <div>
              <h2 className="text-xl font-bold capitalize">{form.tier} {t('loyalty.tierConfig')}</h2>
              <p className="text-sm opacity-70">{existingTier ? t('common.edit') : t('common.create')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/30 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <Star className="h-4 w-4" />
                {t('loyalty.basicInfo')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('loyalty.tierName')} (EN) *</label>
                  <input
                    type="text"
                    value={form.name.en}
                    onChange={(e) => setForm({ ...form, name: { ...form.name, en: e.target.value } })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2"
                    placeholder="e.g., Bronze Member"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('loyalty.tierName')} (TH)</label>
                  <input
                    type="text"
                    value={form.name.th || ''}
                    onChange={(e) => setForm({ ...form, name: { ...form.name, th: e.target.value } })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2"
                    placeholder="e.g., สมาชิกบรอนซ์"
                  />
                </div>
              </div>
            </div>

            {/* Requirements */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                {t('loyalty.requirements')}
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('loyalty.minLifetimePoints')} *</label>
                  <input
                    type="number"
                    value={form.minLifetimePoints}
                    onChange={(e) => setForm({ ...form, minLifetimePoints: Number(e.target.value) })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2"
                    min={0}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('loyalty.minLifetimeSpend')}</label>
                  <input
                    type="number"
                    value={form.minLifetimeSpend || ''}
                    onChange={(e) => setForm({ ...form, minLifetimeSpend: e.target.value ? Number(e.target.value) : undefined })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2"
                    min={0}
                    placeholder="฿"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('loyalty.minOrders')}</label>
                  <input
                    type="number"
                    value={form.minOrdersCount || ''}
                    onChange={(e) => setForm({ ...form, minOrdersCount: e.target.value ? Number(e.target.value) : undefined })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2"
                    min={0}
                  />
                </div>
              </div>
            </div>

            {/* Points Settings */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <Star className="h-4 w-4" />
                {t('loyalty.pointsSettings')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('loyalty.earnRate')} *</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="number"
                      value={form.earnRate}
                      onChange={(e) => setForm({ ...form, earnRate: Number(e.target.value) })}
                      className="w-full rounded-lg border bg-background px-3 py-2"
                      min={0}
                      step={0.1}
                    />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">pts/฿</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">{t('loyalty.earnMultiplier')}</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="number"
                      value={form.earnMultiplier || 1}
                      onChange={(e) => setForm({ ...form, earnMultiplier: Number(e.target.value) })}
                      className="w-full rounded-lg border bg-background px-3 py-2"
                      min={1}
                      step={0.1}
                    />
                    <span className="text-sm text-muted-foreground">x</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">{t('loyalty.redeemRate')} *</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="number"
                      value={form.redeemRate}
                      onChange={(e) => setForm({ ...form, redeemRate: Number(e.target.value) })}
                      className="w-full rounded-lg border bg-background px-3 py-2"
                      min={1}
                    />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">pts = ฿1</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">{t('loyalty.discountPercentage')}</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="number"
                      value={form.discountPercentage || 0}
                      onChange={(e) => setForm({ ...form, discountPercentage: Number(e.target.value) })}
                      className="w-full rounded-lg border bg-background px-3 py-2"
                      min={0}
                      max={100}
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bonuses */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <Gift className="h-4 w-4" />
                {t('loyalty.bonuses')}
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('loyalty.welcomeBonus')}</label>
                  <input
                    type="number"
                    value={form.welcomeBonus || 0}
                    onChange={(e) => setForm({ ...form, welcomeBonus: Number(e.target.value) })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2"
                    min={0}
                    placeholder="pts"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('loyalty.birthdayBonus')}</label>
                  <input
                    type="number"
                    value={form.birthdayBonus}
                    onChange={(e) => setForm({ ...form, birthdayBonus: Number(e.target.value) })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2"
                    min={0}
                    placeholder="pts"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('loyalty.monthlyBonus')}</label>
                  <input
                    type="number"
                    value={form.monthlyBonus || 0}
                    onChange={(e) => setForm({ ...form, monthlyBonus: Number(e.target.value) })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2"
                    min={0}
                    placeholder="pts"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('loyalty.birthdayMultiplier')}</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="number"
                      value={form.birthdayMultiplier || 1}
                      onChange={(e) => setForm({ ...form, birthdayMultiplier: Number(e.target.value) })}
                      className="w-full rounded-lg border bg-background px-3 py-2"
                      min={1}
                      step={0.1}
                    />
                    <span className="text-sm text-muted-foreground">x</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">{t('loyalty.freeUpgrades')}</label>
                  <input
                    type="number"
                    value={form.freeUpgradeCount || 0}
                    onChange={(e) => setForm({ ...form, freeUpgradeCount: Number(e.target.value) })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2"
                    min={0}
                    placeholder="per month"
                  />
                </div>
              </div>
            </div>

            {/* Expiration */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {t('loyalty.expiration')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('loyalty.pointsExpiration')}</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="number"
                      value={form.pointsExpirationMonths || 12}
                      onChange={(e) => setForm({ ...form, pointsExpirationMonths: Number(e.target.value) })}
                      className="w-full rounded-lg border bg-background px-3 py-2"
                      min={1}
                    />
                    <span className="text-sm text-muted-foreground">{t('common.months')}</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">{t('loyalty.gracePeriod')}</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="number"
                      value={form.gracePeriodonths || 3}
                      onChange={(e) => setForm({ ...form, gracePeriodonths: Number(e.target.value) })}
                      className="w-full rounded-lg border bg-background px-3 py-2"
                      min={0}
                    />
                    <span className="text-sm text-muted-foreground">{t('common.months')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <Tag className="h-4 w-4" />
                {t('loyalty.benefits')}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-3 p-3 rounded-lg border hover:bg-secondary/50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.prioritySupport}
                    onChange={(e) => setForm({ ...form, prioritySupport: e.target.checked })}
                    className="rounded"
                  />
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{t('loyalty.prioritySupport')}</span>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg border hover:bg-secondary/50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.exclusivePromotions}
                    onChange={(e) => setForm({ ...form, exclusivePromotions: e.target.checked })}
                    className="rounded"
                  />
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{t('loyalty.exclusivePromotions')}</span>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg border hover:bg-secondary/50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.earlyAccess}
                    onChange={(e) => setForm({ ...form, earlyAccess: e.target.checked })}
                    className="rounded"
                  />
                  <Star className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{t('loyalty.earlyAccess')}</span>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg border hover:bg-secondary/50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.freeDelivery}
                    onChange={(e) => setForm({ ...form, freeDelivery: e.target.checked })}
                    className="rounded"
                  />
                  <Truck className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{t('loyalty.freeDelivery')}</span>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg border hover:bg-secondary/50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.birthdayFreeItem}
                    onChange={(e) => setForm({ ...form, birthdayFreeItem: e.target.checked })}
                    className="rounded"
                  />
                  <Gift className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{t('loyalty.birthdayFreeItem')}</span>
                </label>
              </div>
            </div>

            {/* Display Settings */}
            <div className="space-y-4">
              <h3 className="font-medium">{t('loyalty.displaySettings')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('loyalty.sortOrder')}</label>
                  <input
                    type="number"
                    value={form.sortOrder || 0}
                    onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2"
                    min={0}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('loyalty.color')}</label>
                  <input
                    type="color"
                    value={form.color || '#f97316'}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="w-full mt-1 h-10 rounded-lg border bg-background px-1"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={upsertMutation.isPending || !form.name.en}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {upsertMutation.isPending ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
